// PlanningView.js - Version refactorisée avec tri intelligent pour l'étape actuelle
import React, { useMemo, useState } from 'react';
import { 
  Calendar, Clock, CheckCircle, Plus, ChevronLeft, ChevronRight,
  BarChart3, List, Edit, Award, Move, Zap, Trash2, Share2, Timer
} from 'lucide-react';

const PlanningView = ({
  planning,
  currentWeek,
  selectedDay,
  viewMode,
  isMobile,
  currentMobileDay,
  maxWeeksGenerated,
  draggedAction,
  currentStepId,
  getWeekStartDate,
  getDateForDay,
  canGoToPreviousWeek,
  canGoToNextWeek,
  goToPreviousWeek,
  goToNextWeek,
  goToPreviousDay,
  goToNextDay,
  setSelectedDay,
  setViewMode,
  setDraggedAction,
  setShowAddForm,
  onMoveAction,
  onCompleteAction,
  onEditAction,
  onDeleteAction,
  onGenerateDetails,
  onShareAction,
  isGeneratingDetails,
  editingPriority,
  setEditingPriority,
  onChangePriority,
  isActionShareable
}) => {
  // Fonctions utilitaires
  const isDateToday = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date.getTime() === today.getTime();
  };

  const isActionToday = (action) => {
    if (!action?.date) return false;
    const today = new Date().toISOString().split('T')[0];
    return action.date === today;
  };

  const isActionFuture = (action) => {
    if (!action?.date) return false;
    const today = new Date().toISOString().split('T')[0];
    return action.date > today;
  };

  const parseDuration = (durationStr) => {
    if (!durationStr) return 0;
    
    let durationMinutes = 0;
    
    if (durationStr.includes('heure')) {
      const hourMatch = durationStr.match(/(\d+(?:\.\d+)?)\s*heure/);
      if (hourMatch) {
        durationMinutes += parseFloat(hourMatch[1]) * 60;
      }
    }
    
    if (durationStr.includes('h30') || durationStr.includes('1h30')) {
      durationMinutes += 90;
    } else if (durationStr.includes('h')) {
      const hourMatch = durationStr.match(/(\d+)h/);
      if (hourMatch) {
        durationMinutes += parseInt(hourMatch[1]) * 60;
      }
    }
    
    if (durationStr.includes('minute')) {
      const minuteMatch = durationStr.match(/(\d+)\s*minute/);
      if (minuteMatch) {
        durationMinutes += parseInt(minuteMatch[1]);
      }
    }
    
    if (durationMinutes === 0) {
      const numberMatch = durationStr.match(/(\d+)/);
      if (numberMatch) {
        durationMinutes = parseInt(numberMatch[1]) * 60;
      }
    }
    
    return durationMinutes;
  };

  const formatDuration = (minutes) => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0 ? `${hours}h${remainingMinutes}m` : `${hours}h`;
    }
    return `${minutes}m`;
  };

  // Structure de données unifiée
  const currentWeekData = useMemo(() => {
    const weekData = planning[currentWeek];
    if (!weekData || !weekData.elementaryActions) {
      return { actions: [], actionsByDay: [] };
    }

    const allActions = weekData.elementaryActions;
    const dayNames = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
    
    const actionsByDay = dayNames.map((day, index) => {
      const dayDate = getDateForDay(index, currentWeek);
      const dayActions = allActions.filter(action => action.day === day);
      
      return {
        day,
        date: dayDate.toISOString().split('T')[0],
        actions: dayActions.map(action => ({
          ...action,
          date: dayDate.toISOString().split('T')[0]
        }))
      };
    });

    return {
      ...weekData,
      actionsByDay,
      actions: allActions
    };
  }, [planning, currentWeek, getDateForDay]);

  // Pour la vue liste avec TRI INTELLIGENT : étape actuelle en premier
  const allActionsForList = useMemo(() => {
    if (viewMode !== 'list') return [];
    
    console.log('🔍 PlanningView - Tri des actions pour vue liste');
    console.log('   currentStepId:', currentStepId, 'Type:', typeof currentStepId);
    
    const allActions = [];
    planning.forEach((week, weekIndex) => {
      if (week?.elementaryActions) {
        week.elementaryActions.forEach(action => {
          const dayNames = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
          const dayIndex = dayNames.indexOf(action.day);
          const actionDate = getDateForDay(dayIndex, weekIndex);
          
          allActions.push({
            ...action,
            date: actionDate.toISOString().split('T')[0]
          });
        });
      }
    });
    
    console.log('📋 Actions avant tri:', allActions.length);
    
    // Fonction pour comparer les IDs de manière robuste
    const isSameStep = (actionStepId, targetStepId) => {
      if (!actionStepId || !targetStepId) return false;
      return String(actionStepId).trim() === String(targetStepId).trim();
    };
    
    // TRI INTELLIGENT : Étape actuelle en premier
    const sorted = allActions.sort((a, b) => {
      const today = new Date().toISOString().split('T')[0];
      
      const aIsCurrentStep = isSameStep(a.stepId, currentStepId);
      const bIsCurrentStep = isSameStep(b.stepId, currentStepId);
      
      // 1. PRIORITÉ ABSOLUE : Actions NON complétées de l'étape actuelle en PREMIER
      if (aIsCurrentStep && !a.completed) {
        if (!bIsCurrentStep || b.completed) return -1;
      }
      if (bIsCurrentStep && !b.completed) {
        if (!aIsCurrentStep || a.completed) return 1;
      }
      
      // 2. Si les deux sont de l'étape actuelle et non complétées, trier par date
      if (aIsCurrentStep && bIsCurrentStep && !a.completed && !b.completed) {
        return new Date(a.date) - new Date(b.date);
      }
      
      // 3. Si les deux sont de l'étape actuelle et complétées, trier par date
      if (aIsCurrentStep && bIsCurrentStep && a.completed && b.completed) {
        return new Date(a.date) - new Date(b.date);
      }
      
      // 4. Ensuite, autres actions non complétées (futur avant passé)
      if (!a.completed && !b.completed) {
        const aIsPast = a.date < today;
        const bIsPast = b.date < today;
        
        if (!aIsPast && bIsPast) return -1;
        if (aIsPast && !bIsPast) return 1;
        
        return new Date(a.date) - new Date(b.date);
      }
      
      // 5. Non complétées avant complétées
      if (!a.completed && b.completed) return -1;
      if (a.completed && !b.completed) return 1;
      
      // 6. Les deux complétées, trier par date
      return new Date(a.date) - new Date(b.date);
    });
    
    console.log('✅ TOP 5 actions après tri:', sorted.slice(0, 5).map((a, index) => ({
      position: index + 1,
      title: a.title,
      stepId: a.stepId,
      completed: a.completed,
      date: a.date,
      isCurrentStep: isSameStep(a.stepId, currentStepId)
    })));
    
    const currentStepActionsCount = sorted.filter((a, index) => 
      index < 10 && isSameStep(a.stepId, currentStepId) && !a.completed
    ).length;
    
    console.log(`📊 Actions de l'étape actuelle dans le top 10: ${currentStepActionsCount}`);
    
    return sorted;
  }, [planning, viewMode, getDateForDay, currentStepId]);

  // Statistiques globales pour la vue liste
  const globalStats = useMemo(() => {
    if (viewMode !== 'list') return null;
    
    let totalActions = 0;
    let completedActions = 0;
    let totalDuration = 0;
    
    planning.forEach(week => {
      if (week?.elementaryActions) {
        totalActions += week.elementaryActions.length;
        completedActions += week.elementaryActions.filter(a => a.completed).length;
        week.elementaryActions.forEach(action => {
          totalDuration += parseDuration(action.duration);
        });
      }
    });
    
    return {
      total: totalActions,
      completed: completedActions,
      remaining: totalActions - completedActions,
      percentage: totalActions > 0 ? Math.round((completedActions / totalActions) * 100) : 0,
      totalDuration: formatDuration(totalDuration)
    };
  }, [planning, viewMode]);

  // Statistiques
  const getWeekStats = useMemo(() => {
    const allActions = currentWeekData.actions || [];
    const completed = allActions.filter(action => action.completed).length;
    const total = allActions.length;
    
    const totalDuration = allActions.reduce((acc, action) => acc + parseDuration(action.duration), 0);
    
    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
      totalDuration
    };
  }, [currentWeekData]);

  const getSelectedDayStats = useMemo(() => {
    const dayIndex = isMobile ? currentMobileDay : selectedDay;
    const selectedDayActions = currentWeekData.actionsByDay?.[dayIndex]?.actions || [];
    const totalDuration = selectedDayActions.reduce((acc, action) => acc + parseDuration(action.duration), 0);
    return { totalDuration, formattedDuration: formatDuration(totalDuration) };
  }, [currentWeekData, selectedDay, currentMobileDay, isMobile]);

  const selectedDayActions = viewMode === 'list' 
    ? allActionsForList 
    : currentWeekData.actionsByDay?.[isMobile ? currentMobileDay : selectedDay]?.actions || [];

  // Composant Carte d'Action
  const ActionCard = ({ action }) => {
    const isCurrentStep = action.stepId && currentStepId && String(action.stepId).trim() === String(currentStepId).trim();
    
    return (
      <div 
        className={`p-3 md:p-4 border rounded-lg transition-all ${
          action.completed
            ? 'border-green-300 bg-green-50'
            : isActionToday(action)
            ? 'border-blue-300 bg-blue-50'
            : isActionFuture(action)
            ? 'border-purple-300 bg-purple-50 hover:border-purple-400'
            : 'border-gray-300 bg-gray-100'
        }`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center mb-2">
              <button
                onClick={() => onCompleteAction?.(action.id)}
                className={`mr-3 transition-colors flex-shrink-0 ${
                  action.completed
                    ? 'text-green-600'
                    : isActionToday(action)
                    ? 'text-blue-600 hover:text-blue-700'
                    : isActionFuture(action)
                    ? 'text-purple-600 hover:text-purple-700'
                    : 'text-gray-600 hover:text-gray-700'
                }`}
              >
                <CheckCircle className="h-5 w-5" />
              </button>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-2">
                  <h4 className={`font-medium truncate ${
                    action.completed ? 'text-green-800 line-through' : 
                    isActionToday(action) ? 'text-blue-800' :
                    isActionFuture(action) ? 'text-purple-800' :
                    'text-gray-600'
                  }`} title={action.title}>
                    {action.title}
                  </h4>
                  {isCurrentStep && (
                    <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full whitespace-nowrap">
                      Actuelle
                    </span>
                  )}
                </div>
                {action.description && (
                  <p className="text-sm mt-1 text-gray-600 line-clamp-2">
                    {action.description}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2 md:space-x-3 text-xs flex-wrap gap-y-1">
              {viewMode === 'list' && (
                <span className="flex items-center text-gray-600">
                  <Calendar className="h-3 w-3 mr-1" />
                  {action.day} {new Date(action.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                </span>
              )}
              
              {/* Priorité modifiable */}
              {editingPriority === action.id ? (
                <select
                  value={action.priority}
                  onChange={(e) => onChangePriority?.(action.id, e.target.value)}
                  onBlur={() => setEditingPriority?.(null)}
                  autoFocus
                  className="px-2 py-1 rounded text-xs border border-gray-300 focus:ring-2 focus:ring-blue-500"
                  onClick={(e) => e.stopPropagation()}
                >
                  <option value="high">Haute</option>
                  <option value="medium">Moyenne</option>
                  <option value="low">Basse</option>
                </select>
              ) : (
                <span 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!action.completed) setEditingPriority?.(action.id);
                  }}
                  className={`px-2 py-1 rounded text-xs ${
                    !action.completed ? 'cursor-pointer hover:opacity-80' : ''
                  } ${
                    action.priority === 'high' ? 'bg-red-100 text-red-700' :
                    action.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}
                  title={!action.completed ? "Cliquer pour modifier la priorité" : ""}
                >
                  {action.priority === 'high' ? 'Haute' : 
                   action.priority === 'medium' ? 'Moyenne' : 'Basse'}
                </span>
              )}
              
              <span className="flex items-center text-gray-600">
                <Clock className="h-3 w-3 mr-1" />
                {action.duration}
              </span>
              
              {action.timeSlot && (
                <span className="flex items-center text-gray-600 hidden md:flex">
                  <Timer className="h-3 w-3 mr-1" />
                  {action.timeSlot}
                </span>
              )}
            </div>

            {action.deliverable && (
              <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                <strong>Résultat attendu :</strong> {action.deliverable}
              </div>
            )}
          </div>

          <div className="flex items-center space-x-1 md:space-x-2 ml-2 flex-shrink-0">
            {!action.completed && action.isElementary && (
              <>
                {!action.isDetailed && (
                  <button
                    onClick={() => onGenerateDetails?.(action.id)}
                    disabled={isGeneratingDetails}
                    className="p-1 text-gray-400 hover:text-orange-600 transition-colors disabled:opacity-50"
                    title="Détailler automatiquement"
                  >
                    {isGeneratingDetails ? (
                      <div className="animate-spin h-4 w-4 border-2 border-orange-500 border-t-transparent rounded-full"></div>
                    ) : (
                      <Zap className="h-4 w-4" />
                    )}
                  </button>
                )}
                
                {isActionShareable?.(action) && (
                  <button
                    onClick={() => onShareAction?.(action)}
                    className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
                    title="Partager cette action"
                  >
                    <Share2 className="h-4 w-4" />
                  </button>
                )}
                
                <button
                  onClick={() => onEditAction?.(action)}
                  className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Modifier"
                >
                  <Edit className="h-4 w-4" />
                </button>
                
                <button
                  onClick={() => onDeleteAction?.(action.id)}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  title="Supprimer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                
                {!isMobile && (
                  <div
                    className="p-1 text-gray-400 hover:text-gray-600 cursor-move"
                    title="Glisser pour déplacer"
                  >
                    <Move className="h-4 w-4" />
                  </div>
                )}
              </>
            )}
            
            {action.completed && (
              <div className="flex items-center text-xs text-green-600">
                <Award className="h-3 w-3 mr-1" />
                +{action.priority === 'high' ? '25' : action.priority === 'medium' ? '15' : '10'}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Composant Header optimisé
  const CompactHeader = () => (
    <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
      {/* Desktop Header */}
      <div className="hidden md:flex items-center justify-between px-4 py-3">
        <div className="flex items-center space-x-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Planning Intelligent</h2>
            <p className="text-xs text-gray-600">Actions planifiées automatiquement</p>
          </div>
          
          <div className="flex items-center space-x-4 text-sm">
            {viewMode === 'list' ? (
              <>
                <div className="flex items-center space-x-2 px-3 py-1 bg-blue-50 rounded-lg">
                  <BarChart3 className="h-4 w-4 text-blue-600" />
                  <span className="text-blue-800">{globalStats.completed}/{globalStats.total}</span>
                </div>
                <div className="flex items-center space-x-2 px-3 py-1 bg-purple-50 rounded-lg">
                  <Clock className="h-4 w-4 text-purple-600" />
                  <span className="text-purple-800">{globalStats.totalDuration}</span>
                </div>
                <div className="flex items-center space-x-2 px-3 py-1 bg-green-50 rounded-lg">
                  <span className="text-green-800">{globalStats.remaining} restantes</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center space-x-2 px-3 py-1 bg-blue-50 rounded-lg">
                  <BarChart3 className="h-4 w-4 text-blue-600" />
                  <span className="text-blue-800">
                    {selectedDayActions.filter(a => a.completed).length}/{selectedDayActions.length}
                  </span>
                </div>
                <div className="flex items-center space-x-2 px-3 py-1 bg-purple-50 rounded-lg">
                  <Clock className="h-4 w-4 text-purple-600" />
                  <span className="text-purple-800">{getSelectedDayStats.formattedDuration}</span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {viewMode === 'week' && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  if (selectedDay > 0) {
                    setSelectedDay(selectedDay - 1);
                  } else if (canGoToPreviousWeek()) {
                    goToPreviousWeek();
                    setSelectedDay(6);
                  }
                }}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              
              <div className="text-center min-w-32">
                <div className="text-sm font-medium text-gray-800">
                  {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'][selectedDay]}
                </div>
                <div className="text-xs text-gray-500">
                  {getDateForDay(selectedDay, currentWeek).toLocaleDateString('fr-FR', { 
                    day: 'numeric', 
                    month: 'long' 
                  })}
                </div>
                <div className="text-xs text-purple-600 mt-1">
                  {getSelectedDayStats.formattedDuration}
                </div>
              </div>
              
              <button
                onClick={() => {
                  if (selectedDay < 6) {
                    setSelectedDay(selectedDay + 1);
                  } else if (canGoToNextWeek() || currentWeek < maxWeeksGenerated - 1) {
                    goToNextWeek();
                    setSelectedDay(0);
                  }
                }}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}

          <div className="flex bg-gray-100 rounded-lg p-1">
            {[
              { id: 'calendar', label: 'Calendrier', icon: Calendar },
              { id: 'list', label: 'Liste', icon: List }
            ].map(view => (
              <button
                key={view.id}
                onClick={() => setViewMode(view.id)}
                className={`flex items-center space-x-1 px-3 py-1 rounded text-sm transition-colors ${
                  viewMode === view.id
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <view.icon className="h-4 w-4" />
                <span>{view.label}</span>
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center space-x-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            <Plus className="h-4 w-4" />
            <span>Ajouter</span>
          </button>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Planning</h2>
            <div className="flex items-center space-x-2 text-xs text-gray-600">
              <span>{getWeekStats.completed}/{getWeekStats.total} actions</span>
              <span>•</span>
              <span>{formatDuration(getWeekStats.totalDuration)}</span>
            </div>
          </div>
          
          <button
            onClick={() => setShowAddForm(true)}
            className="p-2 bg-blue-600 text-white rounded-lg"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>

        {viewMode !== 'list' && (
          <div className="flex items-center justify-between">
            <button
              onClick={goToPreviousDay}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            
            <div className="text-center">
              <div className="text-sm font-medium text-gray-800">
                {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'][currentMobileDay]}
              </div>
              <div className="text-xs text-gray-500">
                {getDateForDay(currentMobileDay, currentWeek).toLocaleDateString('fr-FR', { 
                  day: 'numeric', 
                  month: 'long' 
                })}
              </div>
              <div className="text-xs text-purple-600 mt-1">
                {getSelectedDayStats.formattedDuration}
              </div>
            </div>
            
            <button
              onClick={goToNextDay}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Composant Vue Calendrier (reste inchangé)
  const CalendarView = () => {
    const [activeFilter, setActiveFilter] = useState('all');

    const getAllActionsWithDates = () => {
      const actionsMap = new Map();
      
      planning.forEach(week => {
        if (week?.elementaryActions) {
          week.elementaryActions.forEach(action => {
            if (action.date) {
              const dateStr = action.date;
              if (!actionsMap.has(dateStr)) {
                actionsMap.set(dateStr, []);
              }
              actionsMap.get(dateStr).push(action);
            }
          });
        }
      });
      
      return Array.from(actionsMap.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([dateStr, actions]) => {
          const [year, month, day] = dateStr.split('-').map(Number);
          const date = new Date(year, month - 1, day);
          
          return {
            date,
            dateStr,
            actions
          };
        });
    };

    const daysWithActions = getAllActionsWithDates();
    
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const pastDays = daysWithActions.filter(d => d.dateStr < todayStr);
    const todayActions = daysWithActions.find(d => d.dateStr === todayStr);
    const futureDays = daysWithActions.filter(d => d.dateStr > todayStr);

    const shouldShowPast = activeFilter === 'all' || activeFilter === 'past';
    const shouldShowToday = activeFilter === 'all' || activeFilter === 'today';
    const shouldShowFuture = activeFilter === 'all' || activeFilter === 'future';

    const DayCard = ({ date, dateStr, actions, variant = 'default' }) => {
      const colorSchemes = {
        past: {
          container: 'hover:bg-gray-50',
          dateText: 'text-gray-600',
          statsText: 'text-gray-500'
        },
        today: {
          container: 'bg-blue-50 border-l-4 border-blue-500',
          dateText: 'text-blue-900',
          statsText: 'text-blue-700'
        },
        future: {
          container: 'hover:bg-purple-50',
          dateText: 'text-gray-900',
          statsText: 'text-gray-500'
        }
      };

      const colors = colorSchemes[variant];

      return (
        <div id={`date-${dateStr}`} className={`p-4 transition-colors border-b border-gray-100 ${colors.container}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className={`font-semibold ${colors.dateText}`}>
                {date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            </div>
            <div className={`text-sm ${colors.statsText}`}>
              {actions.length} action{actions.length > 1 ? 's' : ''} • {actions.filter(a => a.completed).length} terminée{actions.filter(a => a.completed).length > 1 ? 's' : ''}
            </div>
          </div>
          <div className="space-y-2">
            {actions.map(action => (
              <ActionCard key={action.id} action={action} showWeekNumber={false} />
            ))}
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeFilter === 'all'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Tout afficher
            </button>
            
            <button
              onClick={() => setActiveFilter('past')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeFilter === 'past'
                  ? 'bg-gray-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <div className={`w-2 h-2 rounded-full ${activeFilter === 'past' ? 'bg-white' : 'bg-gray-400'}`}></div>
              Passé ({pastDays.length})
            </button>
            
            <button
              onClick={() => setActiveFilter('today')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeFilter === 'today'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
              }`}
            >
              <div className={`w-2 h-2 rounded-full ${activeFilter === 'today' ? 'bg-white' : 'bg-blue-500'}`}></div>
              Aujourd'hui ({todayActions ? todayActions.actions.length : 0})
            </button>
            
            <button
              onClick={() => setActiveFilter('future')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeFilter === 'future'
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
              }`}
            >
              <div className={`w-2 h-2 rounded-full ${activeFilter === 'future' ? 'bg-white' : 'bg-purple-500'}`}></div>
              À venir ({futureDays.length})
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {shouldShowPast && pastDays.length > 0 && (
            <div>
              {pastDays.map(({ date, dateStr, actions }) => (
                <DayCard 
                  key={dateStr} 
                  date={date} 
                  dateStr={dateStr} 
                  actions={actions} 
                  variant="past" 
                />
              ))}
            </div>
          )}

          {shouldShowToday && todayActions && (
            <DayCard 
              date={todayActions.date} 
              dateStr={todayActions.dateStr} 
              actions={todayActions.actions} 
              variant="today" 
            />
          )}

          {shouldShowFuture && futureDays.length > 0 && (
            <div>
              {futureDays.map(({ date, dateStr, actions }) => (
                <DayCard 
                  key={dateStr} 
                  date={date} 
                  dateStr={dateStr} 
                  actions={actions} 
                  variant="future" 
                />
              ))}
            </div>
          )}

          {((activeFilter === 'past' && pastDays.length === 0) ||
            (activeFilter === 'today' && !todayActions) ||
            (activeFilter === 'future' && futureDays.length === 0)) && (
            <div className="p-8 text-center">
              <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600">
                Aucune action{' '}
                {activeFilter === 'past' ? 'passée' : 
                 activeFilter === 'today' ? 'pour aujourd\'hui' : 
                 'à venir'}
              </p>
            </div>
          )}
        </div>

        {daysWithActions.length === 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-8">
            <div className="text-center">
              <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Aucune action planifiée
              </h3>
              <p className="text-gray-600 mb-4">
                Commencez par ajouter des actions à votre planning
              </p>
              <button
                onClick={() => setShowAddForm(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                + Ajouter une action
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Composant Vue Semaine Optimisée
  const OptimizedWeekView = () => {
    const days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
    
    return (
      <div className="grid grid-cols-7 gap-1 md:gap-2">
        {days.map((day, index) => {
          const dayData = currentWeekData.actionsByDay?.[index];
          const dayActions = dayData?.actions || [];
          const dayDate = getDateForDay(index, currentWeek);
          
          const isToday = isDateToday(dayDate);
          const isSelected = index === selectedDay;
          const completedActions = dayActions.filter(a => a.completed).length;
          
          return (
            <div
              key={day}
              onClick={() => setSelectedDay(index)}
              className={`p-2 md:p-3 border rounded cursor-pointer transition-all ${
                isSelected ? 'border-blue-500 bg-blue-50' :
                isToday ? 'border-green-500 bg-green-50' :
                'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
              style={{ minHeight: isMobile ? '80px' : '120px' }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (draggedAction) {
                  onMoveAction(draggedAction.id, day, currentWeek);
                  setDraggedAction(null);
                }
              }}
            >
              <div className="text-center">
                <h4 className={`text-xs md:text-sm font-medium ${
                  isSelected ? 'text-blue-800' : isToday ? 'text-green-800' : 'text-gray-800'
                }`}>
                  {isMobile ? day.substring(0, 3) : day}
                </h4>
                <div className="text-xs mt-1 text-gray-600">
                  {dayDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                </div>
                
                <div className="mt-1 md:mt-2">
                  <div className="text-xs text-gray-600">
                    {dayActions.length === 0 ? 'Aucune action' : 
                     dayActions.length === 1 ? '1 action' : 
                     `${dayActions.length} actions`}
                  </div>
                  {dayActions.length > 0 && (
                    <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
                      <div 
                        className={`h-1 rounded-full ${
                          completedActions === dayActions.length ? 'bg-green-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${(completedActions / dayActions.length) * 100}%` }}
                      />
                    </div>
                  )}
                </div>
                
                <div className="mt-1 md:mt-2 space-y-1">
                  {dayActions.slice(0, isMobile ? 1 : 2).map((action, i) => (
                    <div
                      key={action.id || i}
                      draggable
                      onDragStart={() => setDraggedAction(action)}
                      className={`text-xs p-1 rounded cursor-move truncate ${
                        action.completed 
                          ? 'bg-green-100 text-green-700 line-through'
                          : isActionToday(action)
                          ? 'bg-blue-100 text-blue-700'
                          : isActionFuture(action)
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                      title={action.title}
                    >
                      {action.title.slice(0, isMobile ? 15 : 20)}
                      {action.title.length > (isMobile ? 15 : 20) && '...'}
                    </div>
                  ))}
                  {dayActions.length > (isMobile ? 1 : 2) && (
                    <div className="text-xs text-gray-500">
                      +{dayActions.length - (isMobile ? 1 : 2)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Rendu principal
  return (
    <>
      <CompactHeader />
      
      <div className="max-w-7xl mx-auto px-4 py-4 md:py-6">
        {!isMobile && (
          <div className="space-y-4">
            {viewMode === 'calendar' && <CalendarView />}
            
            {viewMode !== 'calendar' && (
              <div className="space-y-3">
                {selectedDayActions.length > 0 ? (
                  selectedDayActions.map((action, index) => (
                    <ActionCard 
                      key={action.id || index} 
                      action={action}
                    />
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500 bg-white rounded-lg border border-gray-200 p-4">
                    <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-sm">Aucune action disponible</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {isMobile && (
          <div className="space-y-3">
            {selectedDayActions.length > 0 ? (
              selectedDayActions.map((action, index) => (
                <ActionCard 
                  key={action.id || index} 
                  action={action}
                />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500 bg-white rounded-lg border border-gray-200 p-4">
                <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                <p className="text-sm">Aucune action disponible</p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default PlanningView;